import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import AdminLoginForm from './components/AdminLoginForm';
import SecurityFeatures from './components/SecurityFeatures';

const SystemAdministratorLogin = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>System Administrator Login - TrainaryGym</title>
        <meta name="description" content="Secure authentication portal for system administrators to access enterprise-level platform management functions." />
      </Helmet>

      <div className="min-h-screen relative flex items-center justify-center p-4 md:p-6 lg:p-8 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        {/* Security Pattern Overlay */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-5xl">
          {/* Back to Regular Login */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-all duration-250 focus-ring"
            >
              <Icon name="ArrowLeft" size={16} />
              <span className="text-sm font-medium">Back to Regular Login</span>
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Left Section - Security Information */}
            <div className="flex flex-col justify-center space-y-6 md:space-y-8 text-white">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl bg-red-500/20 border border-red-500/30 flex items-center justify-center">
                    <Icon name="Shield" size={32} color="#ef4444" />
                  </div>
                  <div>
                    <h1 className="text-3xl md:text-4xl font-heading font-bold">
                      System Admin
                    </h1>
                    <p className="text-sm text-gray-400">Enterprise Control Center</p>
                  </div>
                </div>
                <p className="text-base md:text-lg text-gray-300 max-w-md">
                  Secure authentication portal for system administrators. Enhanced security protocols and audit logging enabled.
                </p>
              </div>

              {/* Security Features */}
              <SecurityFeatures />

              {/* Trust Indicators */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-gray-300">
                  <div className="w-10 h-10 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center justify-center">
                    <Icon name="Lock" size={20} color="#22c55e" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">256-bit Encryption</div>
                    <div className="text-xs text-gray-400">Bank-level security</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                    <Icon name="Activity" size={20} color="#3b82f6" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">Real-time Monitoring</div>
                    <div className="text-xs text-gray-400">All access attempts logged</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                    <Icon name="ShieldCheck" size={20} color="#a855f7" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">IP Verification</div>
                    <div className="text-xs text-gray-400">Location-based access control</div>
                  </div>
                </div>
              </div>

              {/* Warning Notice */}
              <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                <div className="flex items-start gap-3">
                  <Icon name="AlertTriangle" size={20} color="#eab308" className="flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-200">
                    <div className="font-semibold mb-1">Authorized Access Only</div>
                    <div className="text-xs text-yellow-300/80">
                      Unauthorized access attempts are monitored and will be reported to security personnel.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Section - Login Form */}
            <div className="flex flex-col justify-center">
              <div className="w-full max-w-md mx-auto">
                <div className="card bg-white dark:bg-gray-800 shadow-2xl">
                  <div className="space-y-2 mb-6 md:mb-8">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                      <span className="text-xs font-semibold text-red-500 uppercase tracking-wider">Secure Session</span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground">
                      Administrator Login
                    </h2>
                    <p className="text-sm md:text-base text-muted-foreground">
                      Enter your system administrator credentials
                    </p>
                  </div>

                  <AdminLoginForm />

                  {/* Emergency Contact */}
                  <div className="mt-6 pt-6 border-t border-border">
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground mb-2">Account locked or need assistance?</p>
                      <button
                        onClick={() => {}}
                        className="text-sm font-medium text-primary hover:text-primary/80 transition-colors duration-250 focus-ring rounded px-3 py-1"
                      >
                        Contact System Support
                      </button>
                    </div>
                  </div>
                </div>

                {/* Session Info */}
                <div className="mt-4 text-center">
                  <p className="text-xs text-gray-400">
                    Session timeout: 15 minutes of inactivity
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SystemAdministratorLogin;